import { format } from "date-fns";
import { FETCH_INVOICE_HISTORY_LIST } from "../../constants";
import { addressingKeyParam } from "../invoices";

export const AVOIR_QUERY_KEYS = {
	list: "LIST_FETCH_AVOIRS",
	detail: "FETCH_AVOIR",
	statuses: "FETCH_AVOIR_STATUSES",
};

const AVOIRS_ENDPOINT = "/invoice/avoirs";

const datesForApi = (avoir) => {
	if (!avoir) return avoir;
	return ["issueDate", "dueDate"].reduce((result, field) => {
		if (!result[field]) return result;
		return { ...result, [field]: format(new Date(result[field]), "dd/MM/yyyy") };
	}, avoir);
};

/**
 * Sérialisation EXPLICITE, alignée sur `fetchInvoices` : le backend attend les dates en
 * `yyyy/MM/dd` (`@DateTimeFormat` sur `AvoirResource`) — un `Date` spreadé partait en ISO et
 * cassait toute la liste en 400, rejoué à chaque montage via le filtre persisté en localStorage.
 * On ne spreade plus le filtre : les champs purement UI (`selectAll`, …) n'ont rien à faire dans
 * la requête.
 */
export const fetchAvoirs = (filter = {}) => {
	const {
		searchBy, status, startIssueDate, endIssueDate, startSaveDate, endSaveDate,
		startCreatedDate, endCreatedDate, pageNo, pageSize, sortBy, periodIntegrated, addressingKey,
	} = filter;
	const dateForApi = (date) => (date ? format(new Date(date), "yyyy/MM/dd") : null);
	return {
		type: AVOIR_QUERY_KEYS.list,
		request: {
			url: AVOIRS_ENDPOINT,
			params: {
				searchBy,
				status: Array.isArray(status)
					? status.map((s) => s?.code || s).join(",")
					: status,
				startIssueDate: dateForApi(startIssueDate),
				endIssueDate: dateForApi(endIssueDate),
				startSaveDate: dateForApi(startSaveDate),
				endSaveDate: dateForApi(endSaveDate),
				startCreatedDate: dateForApi(startCreatedDate),
				endCreatedDate: dateForApi(endCreatedDate),
				pageNo,
				pageSize,
				sortBy,
				periodIntegrated,
				addressingKey: addressingKeyParam(addressingKey),
			},
		},
	};
};

export const fetchAvoir = (id) => ({
	type: AVOIR_QUERY_KEYS.detail,
	request: { url: `${AVOIRS_ENDPOINT}/${id}` },
});

export const fetchAvoirStatuses = () => ({
	type: AVOIR_QUERY_KEYS.statuses,
	request: { url: `${AVOIRS_ENDPOINT}/statuses` },
});

/**
 * Historique d'un avoir. Les lignes d'historique sont porteuses de `avoir_id` (jamais `invoice_id` :
 * la contrainte `ck_history_owner` garantit l'exclusivité), et l'id d'un avoir appartient à la table
 * `avoir` — appeler `/invoice/{id}/history` renvoyait donc un 404 (id inconnu de la table `invoice`).
 * Le type de requête est celui de la facture pour que le composant `History`, partagé entre les deux
 * écrans, lise le résultat via le même `useQuery`.
 */
export const fetchAvoirHistory = (id) => ({
	type: FETCH_INVOICE_HISTORY_LIST,
	request: { url: `${AVOIRS_ENDPOINT}/${id}/history` },
});

export const updateAvoir = (id, avoir) => ({
	type: "UPDATE_AVOIR",
	request: { url: `${AVOIRS_ENDPOINT}/${id}`, method: "put", data: datesForApi(avoir) },
});

export const patchAvoir = (id, fields) => ({
	type: "PATCH_AVOIR",
	request: { url: `${AVOIRS_ENDPOINT}/${id}`, method: "patch", data: datesForApi(fields) },
});

export const proceedAvoir = (id) => ({
	type: "PROCEED_AVOIR",
	request: { url: `${AVOIRS_ENDPOINT}/${id}/proceed`, method: "post" },
	// `onError` fait resoudre la promesse avec l'ERREUR AXIOS ELLE-MEME — exactement ce que fait
	// `workflowProceed` cote factures, et ce que `openProceed` suppose en appelant `serverDetail(res)`.
	//
	// Ce n'est PAS une histoire de promesse rejetee (mesure sur le middleware installe le 01/09/2026) :
	// sans `meta`, la promesse resout deja, mais avec la forme `{ action, error }` — l'erreur est donc
	// imbriquee sous `res.error`, `res.response` est `undefined`, `serverDetail(res)` rend `null` et
	// AUCUN toast ne s'affiche. Avec ce bloc, elle resout avec `{ action, ...erreurAxios }`, donc
	// `res.response` est present. Un refus de « Proceder » -- avoir deja a un statut final ; Billit ne
	// bloque jamais « Approuve » ni « Encaisse » -- laissait sinon l'ecran parfaitement muet, alors que
	// le reseau montrait un 400.
	//
	// ⚠ Contrepartie assumee : le middleware considere l'erreur comme rattrapee et dispatche
	// `PROCEED_AVOIR_SUCCESS` (et non `_ERROR`) en portant l'erreur comme reponse. Sans effet ici,
	// aucun `useQuery`/reducer ne lisant `PROCEED_AVOIR` — et c'est deja le cas de `workflowProceed`,
	// en production cote factures.
	//
	// Le contrat differe volontairement de `createAvoirRejection`, qui RELANCE l'erreur : celle-la
	// enchaine sur `meta.onSuccess` si elle ne le fait pas, et afficherait un toast de succes apres
	// l'echec. Ici `onSuccess` est l'identite, il n'y a rien a proteger.
	meta: {
		onSuccess: (response) => response,
		onError: (error) => error,
	},
});

/**
 * Rejet d'un avoir avec motif. Signature alignée sur `createInvoiceRejection` afin que la boîte de
 * dialogue de sélection du motif (`DialogReject`) soit réutilisée telle quelle : elle appelle
 * `props.createInvoiceRejection`, que l'écran Avoirs pointe sur cette action.
 *
 * Le rechargement final vise `fetchAvoir` et non `fetchInvoice` : c'est tout l'objet de cette copie,
 * l'action des factures rechargeant en dur la facture du même id.
 */
export const createAvoirRejection = (id, rejection, onSuccess, onError) => ({
	type: "CREATE_AVOIR_REJECTION",
	request: {
		url: `${AVOIRS_ENDPOINT}/${id}/reject`,
		method: "post",
		data: rejection,
		headers: { "Content-Type": "application/json" },
	},
	meta: {
		onSuccess: (response, requestAction, store) => {
			if (onSuccess) onSuccess();
			return store.dispatch(fetchAvoir(id));
		},
		// L'erreur est passée au callback, puis RELANCÉE. Le `throw` ne produit pas d'unhandled
		// rejection : redux-requests dispatche l'action d'erreur puis résout sa propre promesse avec
		// `{ action, error }`. Il est en revanche indispensable — sans lui, le middleware considère
		// l'erreur comme rattrapée et enchaîne sur `meta.onSuccess`, ce qui affichait le toast vert
		// « rejeté avec succès » juste après le message d'échec (rejet bloquant refusé par Billit).
		onError: (error, requestAction, store) => {
			if (onError) onError(error);
			store.dispatch(fetchAvoir(id));
			throw error;
		},
	},
});

/**
 * Relance l'extraction OCR d'un avoir.
 *
 * ⚠ Reclassifie : si la nouvelle extraction conclut qu'il s'agit d'une facture, le document quitte
 * la table `avoir` et disparaît donc de cet écran pour réapparaître dans la liste des factures.
 * C'est le comportement attendu, d'où le message de confirmation dédié.
 */
export const relaunchAvoir = (id, onSuccess, onError) => ({
	type: "RELAUNCH_AVOIR",
	request: { url: `${AVOIRS_ENDPOINT}/relaunch/${id}`, method: "put" },
	meta: {
		onSuccess: (response) => {
			if (onSuccess) onSuccess();
			return response;
		},
		// Pas de `throw`, pour la même raison que `createAvoirRejection` ci-dessus : l'unique appelant
		// (`onConfirmRelaunch`, dans le `InvoiceTable` partagé avec les factures) enchaîne sur
		// `.then(...)` sans `catch`. Relancer l'erreur laissait la promesse rejetée — la boîte de
		// confirmation restait bloquée sur son indicateur de chargement et le navigateur signalait une
		// « unhandled rejection ». `relaunchInvoice` ne lève pas non plus : les deux branches de
		// `onConfirmRelaunch` se comportent désormais à l'identique.
		onError: (error) => {
			if (onError) onError(error);
		},
	},
});
